import { useEffect, useRef } from 'react';
import { SCANNER_INPUT_RESET_TIMEOUT_MS } from '../utils/onboardDevicesTable';

const isFormFieldElement = (target: EventTarget | null): target is HTMLElement => {
    if (!(target instanceof HTMLElement)) {
        return false;
    }

    const tagName = target.tagName;
    return target.isContentEditable || tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT';
};

export const useMatrixCodeScanner = (onScan: (scannedCode: string) => void) => {
    const scannerBufferRef = useRef('');
    const lastScannerInputAtRef = useRef<number | null>(null);
    const onScanRef = useRef(onScan);
    onScanRef.current = onScan;

    useEffect(() => {
        const onWindowKeyDown = (event: KeyboardEvent) => {
            if (isFormFieldElement(event.target)) {
                return;
            }

            if (event.key === 'Enter') {
                const scannedCode = scannerBufferRef.current.trim().toUpperCase();
                scannerBufferRef.current = '';
                lastScannerInputAtRef.current = null;

                if (!scannedCode) {
                    return;
                }

                onScanRef.current(scannedCode);
                return;
            }

            if (event.key === 'Escape') {
                scannerBufferRef.current = '';
                lastScannerInputAtRef.current = null;
                return;
            }

            if (event.key === 'Backspace') {
                scannerBufferRef.current = scannerBufferRef.current.slice(0, -1);
                lastScannerInputAtRef.current = Date.now();
                return;
            }

            if (event.ctrlKey || event.metaKey || event.altKey || event.key.length !== 1) {
                return;
            }

            const now = Date.now();
            if (
                lastScannerInputAtRef.current !== null &&
                now - lastScannerInputAtRef.current > SCANNER_INPUT_RESET_TIMEOUT_MS
            ) {
                scannerBufferRef.current = '';
            }

            scannerBufferRef.current += event.key;
            lastScannerInputAtRef.current = now;
        };

        window.addEventListener('keydown', onWindowKeyDown);
        return () => window.removeEventListener('keydown', onWindowKeyDown);
    }, []);
};
